import { useCallback, useMemo } from 'react';

import { ClearFiltersButton } from '@/components/base/clear-filters-button';
import { Select } from '@/components/ui/select';
import {
  Filters as TableFilters,
  useTableContext,
} from '@/contexts/table-context';

import { useOpeningsFilters } from './use-openings-filters';

// Matches TableProvider defaults — Dashboard status starts at "Not added".
const EMPTY_OPENINGS_FILTERS: TableFilters = {
  locationIds: [],
  positionIds: [],
  isSelectedForSourcing: false,
  brandIds: [],
  active: undefined,
  channelIds: [],
  statuses: [],
};

type FiltersProps = {
  /** Called after clear so the parent can remount Search (local input state). */
  onCleared?: () => void;
};

export function Filters({ onCleared }: FiltersProps) {
  const {
    searchQuery,
    setSearchQuery,
    filters: appliedFilters,
    setFilters,
    sorting,
    setSorting,
  } = useTableContext();
  const filters = useOpeningsFilters();

  const handleApply = (
    selectedValues: (string | boolean | undefined)[],
    id: string,
  ) => {
    const isMulti = filters.find(f => f.filterKey === id)?.isMultiSelect;

    setFilters(prev => ({
      ...prev,
      [id]: isMulti ? selectedValues : selectedValues[0],
    }));
  };

  const activeFilterCount = useMemo(() => {
    const multiSelectCount =
      appliedFilters.brandIds.length +
      appliedFilters.positionIds.length +
      appliedFilters.locationIds.length;
    // Default is `false` ("Not added") — only count when the user changed it.
    const dashboardStatusCount =
      appliedFilters.isSelectedForSourcing !== false ? 1 : 0;
    const statusCount = appliedFilters.active !== undefined ? 1 : 0;
    const searchCount = searchQuery?.trim() ? 1 : 0;
    const sortCount = sorting.length > 0 ? 1 : 0;

    return (
      multiSelectCount +
      dashboardStatusCount +
      statusCount +
      searchCount +
      sortCount
    );
  }, [appliedFilters, searchQuery, sorting]);

  const handleClearAll = useCallback(() => {
    setFilters(EMPTY_OPENINGS_FILTERS);
    setSearchQuery(undefined);
    setSorting([]);
    onCleared?.();
  }, [setFilters, setSearchQuery, setSorting, onCleared]);

  return (
    <div className="flex flex-row flex-wrap items-center gap-2">
      {filters.map(filter => (
        <Select
          key={filter.label}
          id={filter.filterKey}
          options={filter.options ?? []}
          selectedValues={filter.selectedValues}
          prefixText={`${filter.label}:`}
          query={filter.query}
          setQuery={filter.setQuery}
          isLoading={filter.isFetching}
          isMultiSelect={filter.isMultiSelect}
          isApplied={
            filter.filterKey === 'isSelectedForSourcing'
              ? appliedFilters.isSelectedForSourcing !== false
              : filter.filterKey === 'active'
                ? appliedFilters.active !== undefined
                : undefined
          }
          onApply={handleApply}
          className="rounded-lg"
        />
      ))}
      <ClearFiltersButton count={activeFilterCount} onClick={handleClearAll} />
    </div>
  );
}
